import { computed, type Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import type {
  Credential,
  CredentialListItem,
  CredentialCsrStarted,
  CredentialCsrText,
  UploadCredentialRequest,
  GenerateCredentialRequest,
  StartCredentialCsrRequest,
} from "@/types";

const BASE = `${import.meta.env.VITE_API_URL}/credentials`;

// READ — list (org-scoped server-side; revoked credentials are never projected).
export function useCredentials() {
  const { data, isFetching: credentialsLoading, error: credentialsError, refetch: refetchCredentials } = useQuery({
    queryKey: ["credentials"],
    queryFn: () => fetchWrapper.get<CredentialListItem[]>(BASE),
  });
  const credentials = computed(() => data.value ?? []);
  return { credentials, credentialsLoading, credentialsError: credentialsError as Ref<ApiError | null>, refetchCredentials };
}

// READ — single (metadata + the connections linking it)
export function useCredential(id: Ref<string>) {
  const { data: credential, isFetching: credentialLoading, error: credentialError } = useQuery({
    queryKey: computed(() => ["credential", id.value]),
    queryFn: () => fetchWrapper.get<Credential>(`${BASE}/${id.value}`),
    enabled: computed(() => !!id.value),
  });
  return { credential, credentialLoading, credentialError: credentialError as Ref<ApiError | null> };
}

// READ — re-derive the pending CSR text (PendingCsr only; the caller gates `enabled`).
export function useCredentialCsr(id: Ref<string>, enabled: Ref<boolean>) {
  const { data, isFetching: csrLoading, error: csrError } = useQuery({
    queryKey: computed(() => ["credential-csr", id.value]),
    queryFn: () => fetchWrapper.get<CredentialCsrText>(`${BASE}/${id.value}/csr`),
    enabled: computed(() => !!id.value && enabled.value),
  });
  const csr = computed(() => data.value?.csr ?? "");
  return { csr, csrLoading, csrError: csrError as Ref<ApiError | null> };
}

// WRITE — BYO upload (pfx+password / SSH keypair / PGP keypair). Material is encrypted at rest.
export function useUploadCredential() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<Credential, ApiError, UploadCredentialRequest>({
    mutationFn: (data) => fetchWrapper.post<Credential>(`${BASE}/upload`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["credentials"] }),
  });
  return {
    uploadCredential: mutate,
    uploadCredentialAsync: mutateAsync,
    uploadCredentialPending: isPending,
    uploadCredentialError: error as Ref<ApiError | null>,
    uploadCredentialReset: reset,
  };
}

// WRITE — generate in place (self-signed X509 / SSH keypair / PGP keypair)
export function useGenerateCredential() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<Credential, ApiError, GenerateCredentialRequest>({
    mutationFn: (data) => fetchWrapper.post<Credential>(`${BASE}/generate`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["credentials"] }),
  });
  return {
    generateCredential: mutate,
    generateCredentialAsync: mutateAsync,
    generateCredentialPending: isPending,
    generateCredentialError: error as Ref<ApiError | null>,
    generateCredentialReset: reset,
  };
}

// WRITE — start a CSR ceremony: server generates + holds the key, returns the CSR for an external CA
export function useStartCredentialCsr() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<CredentialCsrStarted, ApiError, StartCredentialCsrRequest>({
    mutationFn: (data) => fetchWrapper.post<CredentialCsrStarted>(`${BASE}/csr`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["credentials"] }),
  });
  return {
    startCsr: mutate,
    startCsrAsync: mutateAsync,
    startCsrPending: isPending,
    startCsrError: error as Ref<ApiError | null>,
    startCsrReset: reset,
  };
}

// WRITE — complete a CSR ceremony: bind the CA-signed certificate to the held key (one-shot)
export function useCompleteCredentialCsr() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<Credential, ApiError, { id: string; certificatePem: string }>({
    mutationFn: ({ id, certificatePem }) => fetchWrapper.post<Credential>(`${BASE}/${id}/csr/complete`, { certificatePem }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["credentials"] });
      queryClient.invalidateQueries({ queryKey: ["credential", id] });
      queryClient.invalidateQueries({ queryKey: ["credential-csr", id] });
    },
  });
  return {
    completeCsr: mutate,
    completeCsrAsync: mutateAsync,
    completeCsrPending: isPending,
    completeCsrError: error as Ref<ApiError | null>,
    completeCsrReset: reset,
  };
}

// WRITE — rename
export function useRenameCredential() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<Credential, ApiError, { id: string; name: string }>({
    mutationFn: ({ id, name }) => fetchWrapper.put<Credential>(`${BASE}/${id}`, { name }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["credentials"] });
      queryClient.invalidateQueries({ queryKey: ["credential", id] });
    },
  });
  return {
    renameCredential: mutate,
    renameCredentialAsync: mutateAsync,
    renameCredentialPending: isPending,
    renameCredentialError: error as Ref<ApiError | null>,
    renameCredentialReset: reset,
  };
}

// WRITE — rotate material centrally: every linking connection picks up the new material on next use
export function useRotateCredential() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<Credential, ApiError, { id: string; fields: Record<string, string> }>({
    mutationFn: ({ id, fields }) => fetchWrapper.post<Credential>(`${BASE}/${id}/rotate`, { fields }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["credentials"] });
      queryClient.invalidateQueries({ queryKey: ["credential", id] });
    },
  });
  return {
    rotateCredential: mutate,
    rotateCredentialAsync: mutateAsync,
    rotateCredentialPending: isPending,
    rotateCredentialError: error as Ref<ApiError | null>,
    rotateCredentialReset: reset,
  };
}

// WRITE — revoke (delete). The API refuses with a dependents list while any connection links it.
export function useRevokeCredential() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<Credential, ApiError, string>({
    mutationFn: (id) => fetchWrapper.delete<Credential>(`${BASE}/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["credentials"] });
      queryClient.invalidateQueries({ queryKey: ["credential", id] });
    },
  });
  return {
    revokeCredential: mutate,
    revokeCredentialAsync: mutateAsync,
    revokeCredentialPending: isPending,
    revokeCredentialError: error as Ref<ApiError | null>,
    revokeCredentialReset: reset,
  };
}
